/*
 * AI search client for Stiletto. Reads window.AISearch (snippets/ai-search-config.liquid)
 * and exposes window.AISearchClient: service calls plus rendering through the theme's own
 * markup. Products are rendered by fetching the theme's alternate product views
 * (templates/product.ai-card.liquid for the search panel, templates/product.card.liquid
 * for the search page grid), so AI results respect hide_from_search_bar (those views
 * render empty and are dropped). Reviews come from the Okendo store configured in
 * snippets/ai-search-panel.liquid. Fail-soft throughout: calls return null/[] on error
 * so callers fall back to native search.
 */
(function () {
  var cfg = window.AISearch || {};
  var views = {}; // "<view>|<handle>" -> Promise<string|null>; '' = hidden, null = fetch failed
  var panel; // parsed [data-ais-data] (snippets/ai-search-panel.liquid)
  var pool = [], poolDone = null, poolListeners = [];

  function panelData() {
    if (panel === undefined) {
      var el = document.querySelector('[data-ais-data]');
      try { panel = el ? JSON.parse(el.textContent) : null; } catch (e) { panel = null; }
    }
    return panel;
  }

  function ready() {
    return !!(cfg.enabled && cfg.ui && cfg.url && cfg.tenant);
  }

  function base() {
    return cfg.url.replace(/\/+$/, '');
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function formatPrice(price) {
    if (price == null) return '';
    var n = Number(price);
    var whole = n % 1 === 0;
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: cfg.currency || 'USD',
        minimumFractionDigits: whole ? 0 : 2,
        maximumFractionDigits: 2,
      }).format(n);
    } catch (e) {
      return '$' + (whole ? n.toFixed(0) : n.toFixed(2));
    }
  }

  function getJson(path, signal) {
    return fetch(base() + path, { signal: signal }).then(function (res) {
      return res.ok ? res.json() : null;
    });
  }

  function query(term, opts) {
    if (!ready()) return Promise.resolve(null);
    var t = (term || '').trim();
    var path = '/api/search?tenant=' + encodeURIComponent(cfg.tenant) + '&q=' + encodeURIComponent(t);
    return getJson(path, opts && opts.signal).then(function (data) {
      // Drop products the index marks unavailable before any surface renders them.
      return window.AISearchShape ? window.AISearchShape.shapeResult(data, cfg.pins) : data;
    }).catch(function (e) {
      if (e && e.name === 'AbortError') throw e;
      return null;
    });
  }

  // Top queries from the analytics service (terms, or {query, count} when withCounts).
  // Fail-soft -> [].
  function trending(limit, withCounts) {
    if (!ready()) return Promise.resolve([]);
    return getJson('/api/trending?tenant=' + encodeURIComponent(cfg.tenant) + '&limit=' + limit)
      .then(function (d) { return ((d && d.queries) || []).map(function (q) { return withCounts ? q : q.query; }); })
      .catch(function () { return []; });
  }

  // Recent 5-star reviews, optionally scoped to product handles. Fail-soft -> [].
  function reviewsHighlights(limit, handles, q) {
    if (!ready()) return Promise.resolve([]);
    var path = '/api/reviews-highlights?tenant=' + encodeURIComponent(cfg.tenant) + '&limit=' + limit;
    if (handles && handles.length) path += '&products=' + encodeURIComponent(handles.join(','));
    if (q) path += '&q=' + encodeURIComponent(q);
    return getJson(path)
      .then(function (d) { return (d && d.reviews) || []; })
      .catch(function () { return []; });
  }

  // The theme's own markup for one product, via an alternate product view. Cached
  // per page so retyping a query costs no extra requests.
  function fetchView(handle, view) {
    var key = view + '|' + handle;
    if (!views[key]) {
      views[key] = fetch('/products/' + encodeURIComponent(handle) + '?view=' + view)
        .then(function (r) { return r.status === 404 ? '' : r.ok ? r.text() : null; }) // 404: discontinued, drop it
        .then(function (html) { return html == null ? null : html.trim(); })
        .catch(function () { return null; });
    }
    return views[key];
  }

  function aiAttrs(slug, kind, position, surface) {
    return ' data-ai-result data-ai-item="' + escapeHtml(slug) + '" data-ai-kind="' + kind + '"' +
      ' data-ai-position="' + position + '" data-ai-surface="' + escapeHtml(surface) + '"';
  }

  // Stamp the analytics attributes onto the first element of a markup string.
  function stamp(html, attrs) {
    return html.replace(/^<([a-zA-Z0-9-]+)/, '<$1' + attrs);
  }

  // Lean grid card used when the theme view can't be fetched.
  function fallbackCard(item) {
    return '<div class="product-item"><a class="ai-fallback-card" href="' + escapeHtml(item.url) + '">' +
      (item.image ? '<img src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.title) + '" loading="lazy">' : '') +
      '<span class="ai-fallback-card__title">' + escapeHtml(item.title) + '</span>' +
      (item.price != null ? '<span class="ai-fallback-card__price">' + escapeHtml(formatPrice(item.price)) + '</span>' : '') +
      '</a></div>';
  }

  // Lean panel card used when the theme view can't be fetched.
  function fallbackPanelCard(item) {
    return '<a class="ais-card" href="' + escapeHtml(item.url) + '"><span class="ais-card__img">' +
      (item.image ? '<img src="' + escapeHtml(item.image) + '" alt="' + escapeHtml(item.title) + '" loading="lazy">' : '') +
      '</span><span class="ais-card__title">' + escapeHtml(item.title) + '</span>' +
      (item.price != null ? '<span class="ais-card__price">' + escapeHtml(formatPrice(item.price)) + '</span>' : '') + '</a>';
  }
  var fallbacks = { card: fallbackCard, 'ai-card': fallbackPanelCard };

  // Render products through a theme view, in AI order, dropping hidden ones.
  // Resolves to { html, count }.
  function renderProducts(items, view, surface) {
    var fallback = fallbacks[view] || fallbackCard;
    return Promise.all(items.map(function (item) { return fetchView(item.slug, view); }))
      .then(function (marks) {
        var out = [];
        marks.forEach(function (html, i) {
          if (html === '') return; // hidden from search
          out.push(stamp(html == null ? fallback(items[i]) : html,
            aiAttrs(items[i].slug, 'product', out.length + 1, surface)));
        });
        return { html: out.join(''), count: out.length };
      });
  }

  function articleRows(articles, surface, start) {
    return articles.map(function (a, i) {
      return '<a class="quick-search__result" href="' + escapeHtml(a.url) + '"' + aiAttrs(a.slug, 'article', start + i + 1, surface) + '>' +
        '<div class="quick-search__result-heading ff-body fs-body-75">' + escapeHtml(a.title) + '</div></a>';
    }).join('');
  }

  function searchLink(q, cls) {
    return '<a class="' + cls + '" href="/search?q=' + encodeURIComponent(q) + '">' + escapeHtml(q) + '</a>';
  }

  function reviewCards(reviews, surface) {
    return reviews.map(function (r, i) {
      var p = r.product || {};
      return '<a class="ai-review-card" href="' + escapeHtml(p.url) + '"' + aiAttrs(p.handle, 'review', i + 1, surface) + '>' +
        (p.image ? '<img class="ai-review-card__img" src="' + escapeHtml(p.image) + '" alt="' + escapeHtml(p.title) + '" loading="lazy">' : '') +
        '<span class="ai-review-card__content">' +
        '<span class="ai-review-card__product">' + escapeHtml(p.title) + '</span>' +
        '<span class="ai-review-card__stars" aria-label="5 out of 5 stars">★★★★★</span>' +
        '<span class="ai-review-card__body">&ldquo;' + escapeHtml(r.body) + '&rdquo;</span>' +
        '<span class="ai-review-card__meta">' + escapeHtml(r.reviewer) + (r.verified ? ' · Verified buyer' : '') + '</span>' +
        '</span></a>';
    }).join('');
  }

  // --- Reviews: recent Okendo reviews, normalized, cached per browser ---------------

  function normalizeReview(r) {
    var reviewer = r.reviewer || {};
    return {
      title: r.title || '',
      body: r.body || '',
      rating: r.rating || 0,
      date: (r.dateCreated || '').slice(0, 10),
      reviewer: reviewer.displayName || '',
      verified: !!reviewer.isVerified,
      product: { handle: r.productHandle || '', title: r.productName || '', image: r.productImageUrl || '',
        url: r.productHandle ? '/products/' + r.productHandle : '' },
    };
  }

  function store() { try { return window.localStorage; } catch (e) { return null; } }

  function onReviews(fn) { poolListeners.push(fn); }

  // Loads up to rc.pages pages of reviews (newest first), notifying listeners as each
  // page lands so the panel can show reviews before the whole pool is in.
  function loadReviews() {
    if (poolDone) return poolDone;
    var rc = (panelData() || {}).reviews || {};
    if (!rc.store || !rc.api) return (poolDone = Promise.resolve(pool));
    var key = 'ais-reviews:v2:' + rc.store + ':' + rc.pages + ':' + rc.minRating;
    var ls = store(), ttl = (rc.cacheHours || 0) * 3600000;
    try {
      var hit = ls && JSON.parse(ls.getItem(key) || 'null');
      if (hit && ttl && Date.now() - hit.ts < ttl) {
        pool = hit.items;
        return (poolDone = Promise.resolve(pool));
      }
    } catch (e) {}
    function page(path, left) {
      return fetch(rc.api.replace(/\/+$/, '') + path)
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (d) {
          if (!d) return;
          pool = pool.concat((d.reviews || []).map(normalizeReview).filter(function (r) {
            // Links make poor quotes (and are often spam), so those reviews are skipped.
            return r.rating >= rc.minRating && r.body && r.product.handle && !/https?:\/\/|www\./i.test(r.body);
          }));
          poolListeners.forEach(function (fn) { try { fn(pool); } catch (e) {} });
          if (d.nextUrl && left > 1) return page(d.nextUrl, left - 1);
        });
    }
    poolDone = page('/stores/' + encodeURIComponent(rc.store) + '/reviews?limit=100&orderBy=' + encodeURIComponent('date desc'), rc.pages || 1)
      .catch(function () {})
      .then(function () {
        try { if (ls && ttl && pool.length) ls.setItem(key, JSON.stringify({ ts: Date.now(), items: pool })); } catch (e) {}
        return pool;
      });
    return poolDone;
  }

  // Words of a search that carry meaning (stop words from settings removed).
  function termWords(term) {
    var stop = (panelData() || {}).stop || [];
    return String(term || '').toLowerCase().replace(/[^\p{L}\p{N}+\- ]/gu, ' ').split(/\s+/)
      .filter(function (w) { return w.length > 1 && stop.indexOf(w) < 0; });
  }

  function wordPattern(w) {
    return new RegExp('(^|[^\\p{L}\\p{N}])(' + w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '[\\p{L}\\p{N}]*)', 'giu');
  }

  // Reviews mentioning the most search words; reviews of the given products break
  // ties, then the newest wins.
  function matchReviews(term, handles, limit) {
    var words = termWords(term);
    if (!words.length || !limit) return [];
    var pats = words.map(wordPattern);
    handles = handles || [];
    return pool.map(function (r) {
      var text = r.title + ' ' + r.body;
      var hits = pats.filter(function (p) { p.lastIndex = 0; return p.test(text); }).length;
      return { r: r, hits: hits, score: hits * 2 + (handles.indexOf(r.product.handle) >= 0 ? 1 : 0) };
    }).filter(function (x) { return x.hits > 0; })
      .sort(function (a, b) { return b.score - a.score || (a.r.date < b.r.date ? 1 : -1); })
      .slice(0, limit).map(function (x) { return x.r; });
  }

  window.AISearchClient = {
    cfg: cfg,
    ready: ready,
    query: query,
    trending: trending,
    reviewsHighlights: reviewsHighlights,
    escapeHtml: escapeHtml,
    renderProducts: renderProducts,
    panelData: panelData,
    aiAttrs: aiAttrs,
    formatPrice: formatPrice,
    loadReviews: loadReviews,
    onReviews: onReviews,
    reviews: function () { return pool; },
    termWords: termWords,
    wordPattern: wordPattern,
    matchReviews: matchReviews,
    articleRows: articleRows,
    searchLink: searchLink,
    reviewCards: reviewCards,
  };
})();
